import React from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  LoadingOverlay,
  ScrollArea,
  Paper,
  Badge,
  Box,
} from '@mantine/core';
import { IconMessage } from '@tabler/icons-react';
import { Communication, Practitioner } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/common-helpers';
import { formatHumanName } from '@medplum/core';
import { HL7 } from '@consts/systems.consts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ResourceAvatar } from '@medplum/react';
import { defaultMarkdownComponents } from '../../components/MarkdownComponents';

// -----------------------------------------------------------------------------
interface CommunicationWithPractitioner extends Communication {
  practitioner?: Practitioner;
}

// -----------------------------------------------------------------------------
interface CommunicationsCardProps {
  communications: CommunicationWithPractitioner[];
  communicationsLoading: boolean;
}

// -----------------------------------------------------------------------------
export const CommunicationsCard: React.FC<CommunicationsCardProps> = ({
  communications,
  communicationsLoading,
}) => {
  const { t } = useTranslation('lims');

  // ---------------------------------------------------------------------------
  return (
    <Card shadow="sm" padding="md" withBorder>
      <Stack gap="md">
        <Group justify="flex-start" align="center" gap="8px">
          <IconMessage size={20} />
          <Text size="lg" fw={600}>
            {t('review.communications.title')}
          </Text>
          {communicationsLoading && <LoadingOverlay visible />}
        </Group>

        {communications.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {t('review.communications.noCommunications')}
          </Text>
        ) : (
          <ScrollArea h={300}>
            <Stack gap="sm">
              {communications.map((comm) => (
                <Paper key={comm.id} p="sm" withBorder radius="md">
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start">
                      <Group gap="sm">
                        <ResourceAvatar
                          value={comm.practitioner}
                          radius="xl"
                          size="md"
                        />
                        <div>
                          <Text size="sm" fw={600}>
                            {formatHumanName(comm.practitioner?.name?.[0]) ||
                              t('review.communications.unknownUser')}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {comm.sent
                              ? formatDate(comm.sent)
                              : t('review.communications.unknownDate')}
                          </Text>
                        </div>
                      </Group>
                      <Badge
                        size="xs"
                        variant="light"
                        color={
                          comm.category?.[0]?.coding?.[0]?.code ===
                          HL7.TERMINOLOGY.CS.COMMUNICATION.CATEGORY.CODES
                            .INSTRUCTION
                            ? 'blue'
                            : 'green'
                        }
                      >
                        {comm.category?.[0]?.coding?.[0]?.display ||
                          t('review.communications.commentBadge')}
                      </Badge>
                    </Group>
                    <Box ml="36px">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={defaultMarkdownComponents}
                      >
                        {comm.payload?.[0]?.contentString ||
                          t('review.communications.noContent')}
                      </ReactMarkdown>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Stack>
    </Card>
  );
};
